import React from 'react';
import { localize } from 'react-localize-redux';
import styled from 'styled-components';
import Icon, { CloseOutlined, PlusOutlined } from '@ant-design/icons';

import Checkbox from 'components/ui/Checkbox';
import Dropdown from 'components/ui/Dropdown';
import SearchBox from 'components/ui/SearchBox';
import { colors } from 'helpers/ColorHelper';

const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 10px;
  margin-bottom: 20px;
  border-bottom: 1px solid var(--light-periwinkle);
`;

const StyledTitle = styled.span`
  color: var(--dark);
  font-size: 14px;
`;

const StyledClearButton = styled.span`
  color: var(--ultramarine-blue);
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
`;

const StyledCheckboxContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const AddButton = styled.button`
  display: inline-block;
  color: ${(props) => (props.visible ? '#fff' : colors.DARK)};
  background-color: ${(props) => (props.visible ? colors.ULTRAMARINE_BLUE : '#fff')};
  font-size: 10px;
  line-height: 32px;
  height: 34px;
  font-weight: bold;
  text-transform: uppercase;
  border: 1px solid;
  border-color: ${(props) => (props.visible ? colors.ULTRAMARINE_BLUE : colors.LIGHT_PERIWINKLE)};
  border-radius: 28px !important;
  padding-left: 20px;
  padding-right: 35px;
  padding-top: 0;
  padding-bottom: 0;
  text-align: center;
  cursor: pointer;
  position: relative;
  margin-right: 10px;
`;

const StyledIcon = styled(Icon)`
  position: absolute;
  top: 50%;
  right: 15px;
  transform: translateY(-50%);
  font-size: 12px;
`;

const Checkboxes = ({ accentColor, options = [], name, values, handleChange }) => (
  <StyledCheckboxContainer>
    {options.map((option, i) => (
      <Checkbox
        key={`${name}-checkbox-${i}`}
        accentColor={accentColor}
        handleChange={handleChange}
        value={values[name][i]}
        name={`${name}.${i}`}
      >
        {option.replace('_', ' ')}
      </Checkbox>
    ))}
  </StyledCheckboxContainer>
);

const StyledSearchboxContainer = styled.div`
  width: 300px;
`;

const StyledSearchBoxContainer = styled.div`
  padding: 20px 10px;
`;

const StyledSearchResultContainer = styled.div`
  width: 100%;
  height: 170px;
  padding: 0px 10px;
  border-top: 1px solid var(--light-periwinkle);
  overflow-y: scroll;
`;

const StyledLabel = styled.label`
  height: 45px;
  border-bottom: 1px solid var(--light-periwinkle);
  display: ${(props) => (props.visible ? 'flex' : 'none')};
  align-items: center;
  font-size: 13px;
  font-weight: bold;
  cursor: pointer;
`;

const SearchboxDropdown = ({ name, closeDropdown, options, values }) => {
  const [searchTerm, setSearchTerm] = React.useState('');
  const handleChange = (e) => {
    setSearchTerm(e.target.value);
  };
  return (
    <StyledSearchboxContainer>
      <StyledSearchBoxContainer>
        <SearchBox handleChange={handleChange} />
      </StyledSearchBoxContainer>
      <StyledSearchResultContainer>
        {options.map((option, i) => (
          <StyledLabel
            visible={option.toLowerCase().includes(searchTerm.toLowerCase()) && !values[i]}
            htmlFor={`${name}.${i}`}
            key={`searchbox-dropdown-option-${i}`}
          >
            {option}
          </StyledLabel>
        ))}
      </StyledSearchResultContainer>
    </StyledSearchboxContainer>
  );
};

let CheckboxesWithSearch = ({
  accentColor,
  options = [],
  name,
  values,
  handleChange,
  translate,
}) => {
  const [visible, setVisible] = React.useState(false);

  return (
    <StyledCheckboxContainer>
      <Dropdown
        overlay={
          <SearchboxDropdown
            name={name}
            values={values[name]}
            options={options}
            closeDropdown={() => setVisible(false)}
          />
        }
        onVisibleChange={setVisible}
        trigger={['click']}
        visible={visible}
      >
        <AddButton visible={visible} type="button" onClick={() => setVisible(!visible)}>
          {translate('landlordPortal.checkbox.add')}
          <StyledIcon component={visible ? CloseOutlined : PlusOutlined} />
        </AddButton>
      </Dropdown>
      {options.map((option, i) => (
        <Checkbox
          style={values[name][i] ? {} : { display: 'none' }}
          key={`${name}-checkbox-${i}`}
          accentColor={accentColor}
          handleChange={handleChange}
          value={values[name][i]}
          name={`${name}.${i}`}
        >
          {option}
        </Checkbox>
      ))}
    </StyledCheckboxContainer>
  );
};

CheckboxesWithSearch = localize()(CheckboxesWithSearch);

let CheckboxGroup = ({
  useSearch,
  title,
  resetValues,
  values,
  options,
  name,
  setValues,
  translate,
  ...rest
}) => (
  <div>
    <StyledHeader>
      <StyledTitle>{title}</StyledTitle>
      <StyledClearButton
        onClick={() => {
          const _values = {};
          _values[name] = Array(options.length).fill(false);
          setValues({ ...values, ..._values });
        }}
      >
        {translate('landlordPortal.checkbox.clear')}
      </StyledClearButton>
    </StyledHeader>
    {useSearch ? (
      <CheckboxesWithSearch values={values} options={options} name={name} {...rest} />
    ) : (
      <Checkboxes values={values} options={options} name={name} {...rest} />
    )}
  </div>
);

export default localize()(CheckboxGroup);
